import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box } from '@ltvco/refresh-lib/theme';
import { ReportGeneratingScreen } from '@ltvco/refresh-lib/v2';
import { useMemo, useState } from 'react';

import { adaptOralArgumentReport } from 'routes/Dashboard/CourtCases/v2/adapters/adaptOralArgumentReport';
import { JudgeDetails } from 'routes/Dashboard/CourtCases/v2/JudgeSearch';
import {
  DetailsContentSection,
  DetailsTranscriptWrap,
} from 'routes/Dashboard/CourtCases/v2/OralArgumentsSearch/OralArgumentDetails/components';
import { OralArgumentAudio } from 'routes/Dashboard/CourtCases/v2/OralArgumentsSearch/OralArgumentDetails/OralArgumentAudio';
import { OralArgumentDetailsHeader } from 'routes/Dashboard/CourtCases/v2/OralArgumentsSearch/OralArgumentDetails/OralArgumentDetailsHeader';
import { OralArgumentTranscript } from 'routes/Dashboard/CourtCases/v2/OralArgumentsSearch/OralArgumentDetails/OralArgumentTranscript';
import {
  CourtCaseDisclaimer,
  InlineLoading,
  ErrorStateContainer,
  StyledTabsNavigation,
  StyledTab,
} from 'routes/Dashboard/CourtCases/v2/shared';

import type { ReportRouteProps } from './types';

interface OralArgumentReportProps extends ReportRouteProps {}

function hasPresentAudio(
  audio: Record<string, unknown> | null | undefined
): audio is Record<string, unknown> {
  return Boolean(audio && Object.keys(audio).length > 0);
}

function RecordingContent({
  downloadUrl,
  transcript,
}: {
  downloadUrl: string | null;
  transcript: string | null;
}) {
  return (
    <DetailsContentSection>
      <OralArgumentAudio downloadUrl={downloadUrl} />
      <DetailsTranscriptWrap>
        <OralArgumentTranscript transcript={transcript} />
      </DetailsTranscriptWrap>
    </DetailsContentSection>
  );
}

export function OralArgumentReportPage({
  permalink,
  firstReportScreen,
}: OralArgumentReportProps) {
  const [activeTab, setActiveTab] = useState(0);
  const queryResult = useReport(permalink);

  const entity = queryResult.data?.entities?.oral_argument;
  const canAdapt = hasPresentAudio(entity?.audio);

  const viewModel = useMemo(
    () => (entity && canAdapt ? adaptOralArgumentReport(entity) : null),
    [entity, canAdapt]
  );

  const tabs = useMemo(() => {
    if (!viewModel?.judge) {
      return [];
    }

    const { oralArgument, judge } = viewModel;
    return [
      {
        label: 'Recording',
        content: (
          <RecordingContent
            downloadUrl={oralArgument.download_url || null}
            transcript={oralArgument.stt_transcript}
          />
        ),
      },
      {
        label: 'Judge',
        content: (
          <JudgeDetails
            judgeId={String(judge.person.id)}
            person={judge.person}
            positions={judge.positions}
          />
        ),
      },
    ];
  }, [viewModel]);

  const onTabChanged = (newTab: number) => {
    setActiveTab(newTab);
  };

  if (queryResult.isError) {
    return (
      <ErrorStateContainer>
        <p>Oral argument report data is unavailable.</p>
      </ErrorStateContainer>
    );
  }

  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="oral-argument"
        ready={!queryResult.isLoading && Boolean(queryResult.data)}
        onComplete={firstReportScreen.onComplete}
      />
    );
  }

  if (queryResult.isLoading || !queryResult.data) {
    return <InlineLoading />;
  }

  if (!viewModel) {
    return (
      <ErrorStateContainer>
        <p>Oral argument report data is unavailable.</p>
      </ErrorStateContainer>
    );
  }

  const { oralArgument, judge } = viewModel;

  if (!judge) {
    return (
      <Box>
        <OralArgumentDetailsHeader oralArgument={oralArgument} />
        <RecordingContent
          downloadUrl={oralArgument.download_url || null}
          transcript={oralArgument.stt_transcript}
        />
        <CourtCaseDisclaimer />
      </Box>
    );
  }

  return (
    <Box>
      <OralArgumentDetailsHeader oralArgument={oralArgument} />
      <StyledTabsNavigation
        externalActiveTab={activeTab}
        onTabChanged={onTabChanged}
        tab={StyledTab}
        tabs={tabs}
      />
      <CourtCaseDisclaimer />
    </Box>
  );
}
